import * as React from 'react';
import { ChangeEvent } from 'react';

import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { ProfileDocumentFragment, TravellerProfileFragment } from '@websky/graphql';
import { Grid } from '@mui/material';

import { getVisibleFieldError } from '@shared/lib/final-form-utils';
import { useTheme } from '../../../../theme';
import { Input, Dropdown } from '../../../../BaseComponents/index';
import { DateMaskedInput } from '../../../../DateMaskedInput/DateMaskedInput';
import { InputVariant } from '../../../../BaseComponents/Input/types';
import { DropdownOption, TravellerDocumentFields } from '../../../types';
import { useDocumentFields } from '../DocumentFieldsItem/useDocumentFields';

interface Props {
	index: number;
	validators?: ProfileDocumentFragment;
	traveller: TravellerProfileFragment;
	primaryCountryCodes?: string[];
	variant: InputVariant;
	layout: 'grid' | 'stack';
}

const DocumentFormFields: React.FC<Props> = ({
	index,
	validators,
	traveller,
	primaryCountryCodes,
	variant,
	layout
}) => {
	const { DocumentFormFields: css } = useTheme('TravellerForm');

	const { t: tDocumentCardForm } = useTranslation('DocumentCardForm');
	const { t: tTraveller } = useTranslation('Traveller');
	const { t: tPassenger } = useTranslation('Passenger');

	const {
		form,
		citizenshipsByValue,
		citizenshipsOptions,
		documentTypesOptions,
		documentTypesByValue,
		isDocExpireDateHidden,
		isBirthRegDocument,
		citizenshipValidate,
		docTypeValidate,
		docNumberValidate,
		docExpireDateValidate
	} = useDocumentFields(index, validators, traveller, primaryCountryCodes);

	const itemSize = layout === 'grid' ? { xs: 12, sm: 4 } : { xs: 12 };
	const dropdownClasses = variant !== 'labelIn' && { popupIndicator: css.popupIndicator };

	return (
		<Grid className={css.fields__container} container spacing={2} sx={{ flex: 1, minWidth: 0 }}>
			<Grid data-test-id={TravellerDocumentFields.LastName} size={itemSize}>
				<Field
					name={`documents[${index}].${TravellerDocumentFields.LastName}`}
					render={({ input, meta }) => (
						<Input
							{...input}
							className={css.input}
							variant={variant}
							label={`${tTraveller('lastName')}*`}
							error={Boolean(getVisibleFieldError(meta))}
							helperText={getVisibleFieldError(meta) ? tDocumentCardForm(meta.error) : undefined}
							onChange={(event: ChangeEvent<HTMLInputElement>) => {
								input.onChange(event);
								form.mutators.fixEmptyFieldDocumentMutator({
									fieldName: TravellerDocumentFields.LastName,
									index
								});
								form.mutators.documentMutator({ fieldName: TravellerDocumentFields.LastName, index });
							}}
						/>
					)}
				/>
			</Grid>

			<Grid data-test-id={TravellerDocumentFields.FirstName} size={itemSize}>
				<Field
					name={`documents[${index}].${TravellerDocumentFields.FirstName}`}
					render={({ input, meta }) => (
						<Input
							{...input}
							className={css.input}
							variant={variant}
							label={`${tTraveller('firstName')}*`}
							error={Boolean(getVisibleFieldError(meta))}
							helperText={getVisibleFieldError(meta) ? tDocumentCardForm(meta.error) : undefined}
							onChange={(event: ChangeEvent<HTMLInputElement>) => {
								input.onChange(event);
								form.mutators.fixEmptyFieldDocumentMutator({
									fieldName: TravellerDocumentFields.FirstName,
									index
								});
								form.mutators.documentMutator({ fieldName: TravellerDocumentFields.FirstName, index });
							}}
						/>
					)}
				/>
			</Grid>

			<Grid data-test-id={TravellerDocumentFields.MiddleName} size={itemSize}>
				<Field
					name={`documents[${index}].${TravellerDocumentFields.MiddleName}`}
					render={({ input, meta }) => (
						<Input
							{...input}
							className={css.input}
							variant={variant}
							label={tTraveller('middleName')}
							error={Boolean(getVisibleFieldError(meta))}
							helperText={getVisibleFieldError(meta) ? tDocumentCardForm(meta.error) : undefined}
							onChange={(event: ChangeEvent<HTMLInputElement>) => {
								input.onChange(event);
								form.mutators.fixEmptyFieldDocumentMutator({
									fieldName: TravellerDocumentFields.MiddleName,
									index
								});
								form.mutators.documentMutator({ fieldName: TravellerDocumentFields.MiddleName, index });
							}}
						/>
					)}
				/>
			</Grid>

			<Grid data-test-id={TravellerDocumentFields.Citizenship} size={itemSize}>
				<Field
					name={`documents[${index}].${TravellerDocumentFields.Citizenship}`}
					validate={citizenshipValidate}
					render={({ input, meta }) => {
						const selectedOpt = citizenshipsByValue[input.value] || null;

						const onChange = (_: React.ChangeEvent<{}>, value: string | DropdownOption) => {
							const option = value as DropdownOption;
							input.onChange(option === null ? '' : option.value);
							form.mutators.citizenshipMutator({ index });
						};

						return (
							<Dropdown
								classes={dropdownClasses}
								options={citizenshipsOptions}
								value={selectedOpt}
								onChange={onChange}
								disableClearable={true}
								isOptionEqualToValue={(opt, value) =>
									typeof opt !== 'string' && typeof value !== 'string' && opt?.value === value?.value
								}
								InputProps={{
									...input,
									variant,
									label: tTraveller('nationality'),
									error: Boolean(getVisibleFieldError(meta)),
									helperText: getVisibleFieldError(meta) ? tTraveller(meta.error) : undefined
								}}
							/>
						);
					}}
				/>
			</Grid>

			<Grid data-test-id={TravellerDocumentFields.DocType} size={itemSize}>
				<Field
					name={`documents[${index}].${TravellerDocumentFields.DocType}`}
					validate={docTypeValidate}
					render={({ input, meta }) => {
						const selectedOpt = documentTypesByValue[input.value] || null;

						const onChange = (_: React.ChangeEvent<{}>, value: string | DropdownOption) => {
							const option = value as DropdownOption;
							input.onChange(option === null ? '' : option.value);
							form.mutators.docTypeMutator({ index });
						};

						return (
							<Dropdown
								classes={dropdownClasses}
								options={documentTypesOptions}
								value={selectedOpt}
								onChange={onChange}
								disableClearable={true}
								isOptionEqualToValue={(opt, value) =>
									typeof opt !== 'string' && typeof value !== 'string' && opt?.value === value?.value
								}
								InputProps={{
									...input,
									variant,
									label: tDocumentCardForm('Document type'),
									error: Boolean(getVisibleFieldError(meta)),
									helperText: getVisibleFieldError(meta) ? tPassenger(meta.error) : undefined
								}}
							/>
						);
					}}
				/>
			</Grid>

			<Grid data-test-id={TravellerDocumentFields.DocNumber} size={itemSize}>
				<Field
					name={`documents[${index}].${TravellerDocumentFields.DocNumber}`}
					validate={docNumberValidate}
					render={({ input, meta }) => (
						<Input
							{...input}
							className={css.input}
							variant={variant}
							label={`${tDocumentCardForm('Document number')}*`}
							error={Boolean(getVisibleFieldError(meta))}
							helperText={getVisibleFieldError(meta) ? tPassenger(meta.error) : undefined}
							onChange={(event: ChangeEvent<HTMLInputElement>) => {
								if (isBirthRegDocument) {
									event.target.value = event.target.value.toUpperCase();
								}
								input.onChange(event);
								form.mutators.fixEmptyFieldDocumentMutator({
									fieldName: TravellerDocumentFields.DocNumber,
									index
								});
							}}
						/>
					)}
				/>
			</Grid>

			<Grid data-test-id={TravellerDocumentFields.DocExpiryDate} size={itemSize} hidden={isDocExpireDateHidden}>
				<Field
					name={`documents[${index}].${TravellerDocumentFields.DocExpiryDate}`}
					validate={docExpireDateValidate}
					render={({ input, meta }) => (
						<Input
							{...input}
							className={css.input}
							variant={variant}
							label={`${tDocumentCardForm('Expiry date')}*`}
							helperText={getVisibleFieldError(meta) ? tPassenger(meta.error) : undefined}
							error={Boolean(getVisibleFieldError(meta))}
							onChange={(event: ChangeEvent<HTMLInputElement>) => {
								input.onChange(event);
							}}
							InputProps={{
								inputComponent: DateMaskedInput
							}}
						/>
					)}
				/>
			</Grid>
		</Grid>
	);
};

export default DocumentFormFields;
